import type {
  EvidenceFileKind,
  EvidenceFileState,
  EvidenceIntegrity,
  StoredHashScope,
} from "@/types";

/**
 * 증거 원본 화면의 문구. **여기 있는 표현이 이 기능의 본체다.**
 *
 * 세 가지 "없음"(소실 / 저장된 적 없음 / 확인 불가)을 하나로 뭉개면 법정에서
 * 답할 수 없고, 확인하지 않은 것을 "이상 없음"이라고 말하면 없는 검증을
 * 주장하는 것이 된다. 색도 마찬가지다 — `legacy_partial_hash` 에 빨강을 쓰면
 * 백엔드가 하지 않은 변조 판정을 화면이 대신 해 버린다.
 */

export const evidenceKindLabels: Record<EvidenceFileKind, string> = {
  raw: "원본 (단일 업로드)",
  tic: "TIC 원본",
  ms: "MS 원본",
};

export const evidenceStateLabels: Record<EvidenceFileState, string> = {
  available: "보관 중",
  lost: "소실",
  never_stored: "저장된 적 없음",
  not_a_file: "확인 불가",
  out_of_scope: "확인 불가",
};

export const evidenceStateColor = (state: EvidenceFileState): string => {
  switch (state) {
    case "available":
      return "text-green-700 bg-green-100";
    case "lost":
      // 소실은 실제로 나쁜 소식이다 — 여기서는 빨강이 정확하다.
      return "text-red-700 bg-red-100";
    case "never_stored":
      // 결함이 아니라 사실이다. 경고색을 쓰면 없던 증거가 사라진 것처럼 읽힌다.
      return "text-gray-600 bg-gray-100";
    default:
      return "text-amber-700 bg-amber-100";
  }
};

/** 상태가 뜻하는 바를 한 문장으로 — 뱃지만으로는 부족하다. */
export const evidenceStateNote: Record<EvidenceFileState, string> = {
  available: "원본이 저장소에 있으며 내려받을 수 있습니다.",
  lost: "등록 기록은 남아 있으나 저장소에서 파일을 찾을 수 없습니다. 제출할 원본이 없습니다.",
  never_stored:
    "이 종류의 원본은 처음부터 저장되지 않았습니다. 파싱된 결과만 보관되어 있습니다.",
  not_a_file:
    "기록된 경로에 일반 파일이 아닌 것이 있습니다. 기록이 손상되었을 수 있습니다.",
  out_of_scope:
    "기록된 경로가 증거 저장소 밖을 가리킵니다. 안전을 위해 읽지 않았습니다.",
};

export const evidenceIntegrityLabels: Record<EvidenceIntegrity, string> = {
  verified: "무결성 확인됨",
  mismatch: "저장 해시와 불일치",
  legacy_partial_hash: "구 방식 해시 — 일부만 대조됨",
  partially_lost: "짝 파일 소실로 대조 불가",
  unverifiable: "대조 불가",
  lost: "소실",
  never_stored: "저장된 적 없음",
  error: "대조하지 못함",
};

export const evidenceIntegrityColor = (value: EvidenceIntegrity): string => {
  switch (value) {
    case "verified":
      return "text-green-700 bg-green-100";
    case "mismatch":
      // 유일하게 "무언가 잘못됐다"고 말해도 되는 값이다.
      return "text-red-700 bg-red-100";
    default:
      // legacy_partial_hash 포함 — 설명 가능한 정상 기록이거나 판정 불가다.
      // 경고색을 쓰면 백엔드가 하지 않은 변조 판정을 화면이 하게 된다.
      return "text-gray-700 bg-gray-100";
  }
};

/** 무결성 값이 실제로 무엇을 증명하고 무엇을 증명하지 않는지. */
export const evidenceIntegrityNote: Record<EvidenceIntegrity, string> = {
  // ⚠ 이 판정은 **서버가 저장소의 파일을 읽어** 내린 것이다. 브라우저가 받은
  //   바이트에 대한 판정이 아니다 — 전송 무결성은 따로 표시한다.
  verified:
    "저장소의 원본을 내려받는 시점에 서버가 다시 해싱한 값이 등록 당시 해시와 일치합니다.",
  mismatch:
    "다시 해싱한 값이 저장된 해시와 다릅니다. 원본이 바뀌었을 수 있으니 관리자에게 알리세요.",
  legacy_partial_hash:
    "저장된 해시가 TIC·MS 를 이어붙인 값이 아니라 한쪽 파일 단독 해시입니다 — 2026-08-25 이전 업로드 방식의 기록으로 설명되며 변조가 아닙니다. 다만 나머지 한쪽은 어떤 저장 해시로도 덮이지 않아 무결성을 확인할 수 없습니다.",
  partially_lost:
    "짝을 이루는 파일이 소실되어 저장된 해시(두 파일을 이어붙인 값)를 다시 계산할 수 없습니다. 불일치라는 뜻이 아닙니다.",
  unverifiable:
    "저장된 해시가 무엇을 덮는지 단정할 수 없어 대조하지 않았습니다. 불일치라는 뜻이 아닙니다.",
  lost: "원본이 소실되어 대조할 수 없습니다.",
  never_stored: "원본이 저장된 적이 없어 대조할 수 없습니다.",
  error: "원본을 읽지 못해 대조하지 못했습니다.",
};

export const storedHashScopeLabels: Record<StoredHashScope, string> = {
  file: "단일 파일 해시",
  tic_ms_concat: "TIC+MS 이어붙인 해시",
  ambiguous: "범위 불명",
  none: "없음",
};

/** 전송 무결성(브라우저가 받은 바이트 == 서버가 읽은 바이트)의 표현. */
export const transferLabel = (ok: boolean | null): string =>
  ok === true ? "전송 확인됨" : ok === false ? "전송 중 손상" : "전송 미확인";

export const transferColor = (ok: boolean | null): string =>
  ok === true
    ? "text-green-700 bg-green-100"
    : ok === false
      ? "text-red-700 bg-red-100"
      : "text-gray-700 bg-gray-100";

export const transferNote = (ok: boolean | null): string =>
  ok === true
    ? "브라우저가 받은 바이트를 직접 해싱한 값이 서버가 읽은 값과 같습니다 — 전송 중 손상되지 않았습니다."
    : ok === false
      ? "받은 바이트가 서버가 읽은 값과 다릅니다. 전송 중 잘렸거나 손상됐을 수 있으니 다시 받으세요."
      : "받은 바이트를 직접 확인하지 못했습니다 — 이상이 없다는 뜻이 아니라 확인하지 않았다는 뜻입니다.";

export function formatEvidenceSize(bytes: number | null): string {
  if (bytes == null) return "-";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
